import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkle, Play, Pause, Share, PencilSimple, FloppyDisk, Trash,
  Eye, Warning, CheckCircle, XCircle, Info, Waveform, ArrowRight, Bank, FilePdf,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GlassCard } from "@/components/glass/GlassCard";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { PageShell } from "@/components/layout/AppLayout";
import ShareDialog from "@/components/ShareDialog";
import ContractACLCard from "@/components/ContractACLCard";
import api, { API_BASE } from "@/lib/api";
import { openContractPdfPreview } from "@/lib/pdfPreview";
import { toast } from "sonner";

function money(v, ccy = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy, maximumFractionDigits: 0 }).format(v || 0);
}

function StanceBadge({ stance }) {
  const cfg = {
    for: { icon: CheckCircle, cls: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30" },
    against: { icon: XCircle, cls: "text-rose-400", bg: "bg-rose-500/10 border-rose-500/30" },
    neutral: { icon: Info, cls: "text-[var(--text-2)]", bg: "bg-[var(--surface-strong)] border-[var(--border)]" },
  }[stance] || { icon: Info, cls: "text-[var(--text-2)]", bg: "bg-[var(--surface-strong)] border-[var(--border)]" };
  return (
    <span className={`${cfg.bg} ${cfg.cls} text-xs px-2 py-0.5 rounded-full border inline-flex items-center gap-1 uppercase tracking-wide font-semibold`}>
      <cfg.icon size={11} weight="fill" /> {stance}
    </span>
  );
}

export default function ContractDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [contract, setContract] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [shareOpen, setShareOpen] = useState(false);
  const [ripple, setRipple] = useState(null);
  const [loanSummary, setLoanSummary] = useState(null);
  const [loanLoading, setLoanLoading] = useState(false);
  const [forceShowLoan, setForceShowLoan] = useState(false);
  const audioRef = React.useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/contracts/${id}`);
      setContract(data);
      setForm(data);
      try {
        const a = await api.get(`/analyze/${id}`);
        setAnalysis(a.data);
      } catch {}
      try {
        const ls = await api.get(`/loan/summary/${id}`);
        setLoanSummary(ls.data?.summary || null);
      } catch {}
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  useEffect(() => {
    if (!id) return;
    api.get(`/contracts/${id}/ripple`).then(({ data }) => setRipple(data)).catch(() => {});
  }, [id]);

  const runAnalysis = async () => {
    setAnalyzing(true);
    toast.info("Claude Sonnet 4.5 is analyzing your contract…");
    try {
      const { data } = await api.post("/analyze", { contract_id: id, perspective: "signatory" });
      setAnalysis(data);
      await load();
      toast.success("Analysis complete");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally { setAnalyzing(false); }
  };

  const fetchAudio = async () => {
    setAudioLoading(true);
    try {
      const token = localStorage.getItem("clariva_token");
      const res = await fetch(`${API_BASE}/audio-briefing/${id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("TTS failed");
      const blob = await res.blob();
      setAudioUrl(URL.createObjectURL(blob));
      toast.success("Audio briefing ready");
    } catch (e) {
      toast.error("Audio briefing failed");
    } finally { setAudioLoading(false); }
  };

  const runLoanSummary = async () => {
    setLoanLoading(true);
    toast.info("Extracting loan terms with Claude 4.5…");
    try {
      const { data } = await api.post("/loan/summarize", { contract_id: id, loan_type: "auto" });
      setLoanSummary(data.summary);
      toast.success("Loan terms extracted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Loan summary failed");
    } finally { setLoanLoading(false); }
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) audioRef.current.pause(); else audioRef.current.play();
    setPlaying(!playing);
  };

  const save = async () => {
    const patch = {
      title: form.title, counterparty: form.counterparty,
      value_amount: Number(form.value_amount) || 0,
      value_currency: form.value_currency,
      content: form.content,
    };
    const { data } = await api.patch(`/contracts/${id}`, patch);
    setContract(data); setEditing(false);
    toast.success("Saved");
  };

  const del = async () => {
    if (!confirm("Delete this contract?")) return;
    await api.delete(`/contracts/${id}`);
    toast.success("Deleted");
    nav("/app/repository");
  };

  const sendForSignature = () => {
    setShareOpen(true);
  };

  if (loading || !contract) {
    return <PageShell title="Contract" subtitle="Loading"><div className="h-64 glass animate-pulse" /></PageShell>;
  }

  return (
    <PageShell
      title={contract.title}
      subtitle={contract.counterparty}
      right={
        <>
          <Button variant="outline" size="sm" onClick={() => openContractPdfPreview(id)} data-testid="preview-pdf-btn" className="gap-2 glass-subtle border-[var(--border-strong)]">
            <FilePdf size={14} weight="duotone" /> Preview PDF
          </Button>
          <Button variant="outline" size="sm" onClick={sendForSignature} data-testid="send-signature-btn" className="gap-2 glass-subtle border-[var(--border-strong)]">
            <Share size={14} /> Send for signature
          </Button>
          {editing ? (
            <Button size="sm" onClick={save} data-testid="save-contract-btn" className="gap-2">
              <FloppyDisk size={14} /> Save
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid="edit-contract-btn" className="gap-2">
              <PencilSimple size={14} /> Edit
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={del} data-testid="delete-contract-btn" className="text-[var(--danger)]">
            <Trash size={14} />
          </Button>
        </>
      }
    >
      {/* Meta grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <GlassCard className="p-4">
          <div className="overline mb-1">Status</div>
          <div className="capitalize text-sm font-medium">{contract.status?.replace("_", " ")}</div>
        </GlassCard>
        <GlassCard className="p-4">
          <div className="overline mb-1">Value</div>
          <div className="text-sm font-mono font-medium">{money(contract.value_amount, contract.value_currency)}</div>
        </GlassCard>
        <GlassCard className="p-4">
          <div className="overline mb-1">Risk</div>
          <div><span className={`chip chip-${contract.risk_level || "unknown"}`}>{contract.risk_level || "not analyzed"}</span></div>
        </GlassCard>
        <GlassCard className="p-4">
          <div className="overline mb-1">Expiration</div>
          <div className="text-sm">{contract.expiration_date || "—"}</div>
        </GlassCard>
      </div>

      {/* Per-contract access control (admins/contract_managers only) */}
      <div className="mb-6">
        <ContractACLCard contractId={contract.id} ownerId={contract.owner_id} ownerEmail={contract.owner_email} />
      </div>

      <Tabs defaultValue="analysis" className="space-y-6">
        <TabsList className="glass-subtle p-1">
          <TabsTrigger value="analysis" data-testid="tab-analysis">AI Analysis</TabsTrigger>
          <TabsTrigger value="text" data-testid="tab-text">Contract Text</TabsTrigger>
          <TabsTrigger value="obligations" data-testid="tab-obligations">Obligations</TabsTrigger>
          <TabsTrigger value="loopholes" data-testid="tab-loopholes">Loopholes</TabsTrigger>
          <TabsTrigger value="ripple" data-testid="tab-ripple">Ripple Analysis</TabsTrigger>
        </TabsList>

        <TabsContent value="analysis" className="space-y-6">
          {!analysis && (
            <GlassCard className="p-12 text-center">
              <Sparkle size={32} weight="duotone" className="mx-auto mb-4 text-[var(--accent)]" />
              <h3 className="text-2xl font-medium mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Run AI Analysis</h3>
              <p className="text-sm text-[var(--text-2)] max-w-md mx-auto mb-6">
                Claude Sonnet 4.5 will analyze every clause from your perspective, identify loopholes, and extract obligations.
              </p>
              <Button onClick={runAnalysis} disabled={analyzing} data-testid="run-analysis-btn" size="lg" className="gap-2">
                <Sparkle size={16} weight="fill" />
                {analyzing ? "Analyzing with Claude…" : "Analyze with AI"}
              </Button>
            </GlassCard>
          )}

          {analysis && (
            <>
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <GlassCard strong className="p-6 md:p-8">
                  <div className="flex items-center gap-2 overline mb-3">
                    <Sparkle size={12} weight="fill" className="text-[var(--accent)]" /> AI Summary
                  </div>
                  <p className="text-lg leading-relaxed text-[var(--text)]" style={{ fontFamily: 'var(--font-heading)', fontWeight: 400 }}>
                    {analysis.plain_language_summary}
                  </p>
                  <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <div className="overline">Risk</div>
                      <div className="mt-1"><span className={`chip chip-${analysis.risk_level}`}>{analysis.risk_level}</span></div>
                    </div>
                    <div>
                      <div className="overline">Score</div>
                      <div className="mt-1 text-2xl font-mono font-semibold">{analysis.risk_score}/100</div>
                    </div>
                    <div>
                      <div className="overline">Clauses</div>
                      <div className="mt-1 text-2xl font-mono font-semibold">{analysis.clauses?.length || 0}</div>
                    </div>
                    <div>
                      <div className="overline">Loopholes</div>
                      <div className="mt-1 text-2xl font-mono font-semibold">{analysis.loopholes?.length || 0}</div>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>

              {/* Audio briefing */}
              <GlassCard className="p-6">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1 h-12">
                    {[0,1,2,3,4,5,6].map((i) => (
                      <span
                        key={i}
                        className="wave-bar"
                        style={{ animationDelay: `${i * 0.08}s`, opacity: playing ? 1 : 0.35 }}
                      />
                    ))}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="overline">Audio briefing</div>
                    <div className="text-sm text-[var(--text-2)]">60-second executive summary narrated by OpenAI TTS</div>
                  </div>
                  {audioUrl ? (
                    <>
                      <audio ref={audioRef} src={audioUrl} onEnded={() => setPlaying(false)} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} />
                      <Button onClick={togglePlay} data-testid="audio-play-btn" className="gap-2">
                        {playing ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />}
                        {playing ? "Pause" : "Play"}
                      </Button>
                    </>
                  ) : (
                    <Button onClick={fetchAudio} disabled={audioLoading} data-testid="generate-audio-btn" className="gap-2">
                      <Waveform size={14} />
                      {audioLoading ? "Generating…" : "Generate briefing"}
                    </Button>
                  )}
                </div>
              </GlassCard>

              {/* Bank Loan Summary — auto-shown if loan-ish content or already extracted, otherwise via toggle */}
              {(() => {
                const looksLikeLoan = /loan|mortgage|credit|emi|borrower|lender|interest rate|principal|collateral|apr/i.test((contract?.content || "").slice(0, 2000));
                const show = loanSummary || looksLikeLoan || forceShowLoan;
                return show ? (
                <GlassCard className="p-6" data-testid="loan-summary-card">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-md bg-lime-500/10 border border-lime-500/30 flex items-center justify-center">
                      <Bank size={18} weight="duotone" className="text-lime-400" />
                    </div>
                    <div className="flex-1">
                      <div className="overline">Bank loan summary</div>
                      <div className="text-sm text-[var(--text-2)]">Structured extraction of APR, EMI, fees, covenants, and red flags.</div>
                    </div>
                    {!loanSummary && (
                      <Button onClick={runLoanSummary} disabled={loanLoading} data-testid="run-loan-summary-btn" className="gap-2">
                        <Sparkle size={14} weight="fill" />
                        {loanLoading ? "Extracting…" : "Summarize loan"}
                      </Button>
                    )}
                  </div>

                  {loanSummary && (
                    <div className="space-y-4">
                      <p className="text-sm leading-relaxed">{loanSummary.tldr}</p>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="p-3 rounded-md bg-[var(--surface)] border border-[var(--border)]">
                          <div className="overline">Principal</div>
                          <div className="text-base font-mono font-semibold mt-1">
                            {loanSummary.principal?.currency || ""} {(loanSummary.principal?.amount || 0).toLocaleString()}
                          </div>
                        </div>
                        <div className="p-3 rounded-md bg-[var(--surface)] border border-[var(--border)]">
                          <div className="overline">APR</div>
                          <div className="text-base font-mono font-semibold mt-1">{loanSummary.interest?.apr_percent ?? "—"}%</div>
                          <div className="text-[10px] text-[var(--text-3)] mt-0.5 capitalize">{loanSummary.interest?.type || ""}</div>
                        </div>
                        <div className="p-3 rounded-md bg-[var(--surface)] border border-[var(--border)]">
                          <div className="overline">Tenure</div>
                          <div className="text-base font-mono font-semibold mt-1">{loanSummary.tenure?.months ?? "—"}<span className="text-xs ml-1">mo</span></div>
                        </div>
                        <div className="p-3 rounded-md bg-[var(--surface)] border border-[var(--border)]">
                          <div className="overline">EMI</div>
                          <div className="text-base font-mono font-semibold mt-1">{loanSummary.principal?.currency || ""} {(loanSummary.tenure?.emi_amount || 0).toLocaleString()}</div>
                        </div>
                      </div>

                      {!!(loanSummary.risk_flags || []).length && (
                        <div>
                          <div className="overline mb-2">Risk flags</div>
                          <div className="space-y-2">
                            {loanSummary.risk_flags.map((r, i) => (
                              <div key={`${r.severity}-${r.flag}-${i}`} className={`p-3 rounded-md border text-xs ${
                                r.severity === "critical" ? "border-red-500/40 bg-red-500/5" :
                                r.severity === "high" ? "border-orange-500/40 bg-orange-500/5" :
                                r.severity === "medium" ? "border-amber-500/30 bg-amber-500/5" :
                                "border-[var(--border)] bg-[var(--surface)]"
                              }`}>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="uppercase text-[10px] tracking-widest font-semibold">{r.severity}</span>
                                  <span className="font-semibold">{r.flag}</span>
                                </div>
                                <div className="text-[var(--text-2)] leading-snug">{r.why_it_matters}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {!!(loanSummary.questions_to_ask_banker || []).length && (
                        <div>
                          <div className="overline mb-2">Questions to ask your banker</div>
                          <ul className="text-xs space-y-1 list-disc list-inside text-[var(--text-2)]">
                            {loanSummary.questions_to_ask_banker.map((q, i) => <li key={`q-${i}-${q.slice(0, 20)}`}>{q}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </GlassCard>
                ) : (
                  <GlassCard className="p-4 flex items-center gap-3" data-testid="loan-summary-reveal-row">
                    <Bank size={18} weight="duotone" className="text-lime-400" />
                    <div className="flex-1 text-xs text-[var(--text-2)]">Is this a bank loan, mortgage, or credit agreement? Extract structured financial terms.</div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setForceShowLoan(true)}
                      data-testid="loan-summary-reveal-btn"
                      className="gap-1.5"
                    >
                      <Sparkle size={12} /> Treat as loan
                    </Button>
                  </GlassCard>
                );
              })()}

              {/* Clauses */}
              <div>
                <div className="overline mb-3">Clause-by-clause — from signatory perspective</div>
                <div className="space-y-3">
                  {(analysis.clauses || []).map((c, i) => (
                    <motion.div
                      key={c.id || i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.04 }}
                    >
                      <GlassCard className="p-5" hover>
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <StanceBadge stance={c.stance} />
                              <span className="chip chip-neutral">{c.category}</span>
                              <span className={`chip chip-${c.risk_score >= 75 ? "critical" : c.risk_score >= 50 ? "high" : c.risk_score >= 25 ? "medium" : "low"}`}>
                                Risk {c.risk_score}
                              </span>
                            </div>
                            <h4 className="text-base font-medium" style={{ fontFamily: 'var(--font-heading)' }}>{c.title}</h4>
                          </div>
                        </div>
                        <div className="text-sm leading-relaxed text-[var(--text-2)] mb-2 italic">"{c.text}"</div>
                        <div className="text-sm leading-relaxed mt-3 p-3 rounded-md bg-[var(--surface)] border border-[var(--border)]">
                          <div className="overline mb-1">Plain English</div>
                          {c.plain_language}
                        </div>
                        {c.explanation && (
                          <div className="text-sm leading-relaxed mt-2 text-[var(--text-2)]">
                            <span className="font-semibold">Why it matters: </span>{c.explanation}
                          </div>
                        )}
                      </GlassCard>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={() => nav(`/app/impact/${id}`)} data-testid="go-to-impact-btn" variant="outline" className="glass-subtle border-[var(--border-strong)] gap-2">
                  View Business Impact <ArrowRight size={14} />
                </Button>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="text">
          <GlassCard className="p-6">
            {editing ? (
              <div className="space-y-4">
                <div>
                  <div className="overline mb-1">Title</div>
                  <Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="edit-title-input" className="glass-subtle border-[var(--border)]" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="overline mb-1">Counterparty</div>
                    <Input value={form.counterparty || ""} onChange={(e) => setForm({ ...form, counterparty: e.target.value })} data-testid="edit-counterparty-input" className="glass-subtle border-[var(--border)]" />
                  </div>
                  <div>
                    <div className="overline mb-1">Value</div>
                    <Input type="number" value={form.value_amount || ""} onChange={(e) => setForm({ ...form, value_amount: e.target.value })} data-testid="edit-value-input" className="glass-subtle border-[var(--border)]" />
                  </div>
                </div>
                <div>
                  <div className="overline mb-1">Contract text</div>
                  <Textarea rows={20} value={form.content || ""} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="edit-content-textarea" className="glass-subtle border-[var(--border)] font-mono text-xs" />
                </div>
              </div>
            ) : (
              <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-[var(--text-2)] max-h-[70vh] overflow-y-auto">
                {contract.content || "No content."}
              </pre>
            )}
          </GlassCard>
        </TabsContent>

        <TabsContent value="obligations">
          <GlassCard className="p-6">
            {(analysis?.obligations || []).length === 0 && <div className="text-sm text-[var(--text-3)] text-center py-8">Run AI analysis to extract obligations.</div>}
            <div className="space-y-2">
              {(analysis?.obligations || []).map((o, i) => (
                <div key={o.id || `ob-${o.title}-${o.due || ""}-${i}`} className="p-4 rounded-md bg-[var(--surface)] border border-[var(--border)] flex items-start gap-3">
                  <span className={`chip chip-${o.priority || "medium"} mt-0.5`}>{o.priority || "medium"}</span>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{o.title}</div>
                    <div className="text-xs text-[var(--text-2)] mt-1">{o.description}</div>
                    <div className="text-xs text-[var(--text-3)] mt-1">{o.owner} · {o.due || "—"}</div>
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
        </TabsContent>

        <TabsContent value="loopholes">
          <GlassCard className="p-6">
            {(analysis?.loopholes || []).length === 0 && <div className="text-sm text-[var(--text-3)] text-center py-8">Run AI analysis to detect loopholes.</div>}
            <div className="space-y-3">
              {(analysis?.loopholes || []).map((l, i) => (
                <div key={l.id || `lh-${l.clause_ref || ""}-${(l.description || "").slice(0, 30)}-${i}`} className="p-4 rounded-md bg-[var(--surface)] border border-[var(--border)] flex items-start gap-3">
                  <Warning size={18} weight="duotone" className="text-amber-400 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`chip chip-${l.severity}`}>{l.severity}</span>
                      <span className="text-xs text-[var(--text-3)]">{l.clause_ref}</span>
                    </div>
                    <div className="text-sm">{l.description}</div>
                    {l.suggestion && <div className="text-xs text-[var(--text-2)] mt-2"><span className="font-semibold">Suggestion: </span>{l.suggestion}</div>}
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
        </TabsContent>
      </Tabs>
      <ShareDialog contractId={id} open={shareOpen} onOpenChange={setShareOpen} />
    </PageShell>
  );
}
